import { token } from '@betterment/samba-styled-system/tokens';
import { type XYChartTheme, buildChartTheme } from '@visx/xychart';

export type ThemeConfig = Parameters<typeof buildChartTheme>[0];

/**
 * Creates a chart theme that uses Samba design system tokens.
 *
 * Configures default colors, typography, grid lines, and axis styles consistent with
 * the Samba design system. All theme properties can be overridden by passing a partial config.
 *
 * @example
 * ```tsx
 * const theme = createSambaChartTheme({
 *   colors: ['#1E88E5', '#43A047'],
 * });
 *
 * <ChartProvider theme={theme} xScale={xScale} yScale={yScale}>
 *   <XYChart width={500} height={300}>
 *     {children}
 *   </XYChart>
 * </ChartProvider>
 * ```
 */
export const createSambaChartTheme = (theme?: Partial<ThemeConfig>): XYChartTheme => {
  return buildChartTheme({
    backgroundColor: 'transparent',
    colors: [
      token('colors.dataviz.blue.2'),
      token('colors.dataviz.teal.1'),
      token('colors.dataviz.orange.1'),
      token('colors.dataviz.purple.1'),
      token('colors.dataviz.navy.1'),
    ],
    gridColor: token('colors.border.weak'),
    gridColorDark: token('colors.border.weak'),
    xAxisLineStyles: {
      strokeWidth: 1,
      stroke: token('colors.border.weak'),
    },
    yAxisLineStyles: {
      strokeWidth: 1,
      stroke: token('colors.border.weak'),
    },
    svgLabelSmall: {
      fill: token('colors.text.weak'),
      fontSize: token('fontSizes.fontSize.25'),
      fontFamily: token('fonts.gtAmerica'),
      fontWeight: token('fontWeights.regular'),
    },
    svgLabelBig: {
      fill: token('colors.text.weak'),
      fontSize: token('fontSizes.fontSize.25'),
      fontFamily: token('fonts.gtAmerica'),
      fontWeight: token('fontWeights.regular'),
    },
    tickLength: 4,
    ...theme,
  });
};
